import type { FallbackRender } from '@sentry/react';
import TrueLogo from 'src/assets/true-logo.svg?react';

/**
 * Detects if an error is caused by a failed chunk/module load
 * This typically happens when a new deployment replaces old JS chunks
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const errorObj = error as Error;
  const message = errorObj.message || '';
  const name = errorObj.name || '';

  return (
    // Vite/ESM dynamic import errors
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Unable to preload CSS') ||
    // Webpack chunk errors
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError') ||
    name === 'ChunkLoadError' ||
    // Generic module loading failures
    message.includes('Failed to load module') ||
    message.includes('error loading dynamically imported module')
  );
}

const handleRefresh = () => {
  globalThis.location.reload();
};

const ErrorFallback: FallbackRender = ({ error }) => {
  const isNewVersionAvailable = isChunkLoadError(error);

  return (
    <div className="size-full p-4 flex flex-col items-center justify-center text-center">
      <TrueLogo className="w-50 max-w-full mb-4" />

      {isNewVersionAvailable ? (
        <>
          <h3 className="text-xl font-semibold mb-2">
            A new version is available
          </h3>
          <p className="text-lg text-gray-600 mb-4">
            Please refresh to continue.
          </p>
          <button
            type="button"
            onClick={handleRefresh}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Refresh Page
          </button>
        </>
      ) : (
        <>
          <h3 className="text-xl font-semibold mb-2">Something went wrong.</h3>
          <p className="text-lg">
            We've been notified.{' '}
            <button
              type="button"
              onClick={handleRefresh}
              className="underline cursor-pointer text-blue-600 hover:text-blue-800"
            >
              Refresh the page
            </button>{' '}
            to try again.
          </p>
        </>
      )}
    </div>
  );
};

export default ErrorFallback;
